import { z } from 'zod'

import { subjectType } from './auth.js'
import { email, role } from './common.js'

/** 가입 승인 상태(BIZ-68). 후불 자격과는 무관하다 — 그건 postpaidStatus(BIZ-149). */
export const approvalStatus = z.enum(['PENDING', 'APPROVED', 'REJECTED'])
export type ApprovalStatus = z.infer<typeof approvalStatus>

/**
 * 후불 자격(BIZ-149). NONE 선불 / REQUESTED 후불 신청(운영자 검토 대기) / ACTIVE 후불(월청구) /
 * SUSPENDED 미납 자동정지(완납하면 자동 재개) / REJECTED 신청 반려(다시 신청 가능).
 * 후불(ACTIVE)이 아니면 주문은 선불 결제가 필요하다.
 */
export const postpaidStatus = z.enum(['NONE', 'REQUESTED', 'ACTIVE', 'SUSPENDED', 'REJECTED'])
export type PostpaidStatus = z.infer<typeof postpaidStatus>

/**
 * 조직(기업·업체)과 초대 API 계약.
 *
 * 운영자가 기업·화환 업체를 등록하고, 그 조직의 사용자를 초대한다.
 * 근거: 설계 명세서 1.2, 5.1 · 기획서 9.5, 9.7
 */

// ── 공통 ────────────────────────────────────────────────────

/** 사업자등록번호. 하이픈 유무를 받되 저장은 하이픈 포함 표준형으로 정규화한다. */
const bizRegNo = z
  .string()
  .transform((v) => v.replace(/[^0-9]/g, ''))
  .refine((v) => v.length === 10, '사업자등록번호는 10자리입니다')

/**
 * 선택 전화번호. 빈 문자열은 "입력 안 함"으로 본다.
 *
 * 예전엔 refine이 빈 문자열을 통과시켜, 도메인에서 `'' ?? null`이 ''을 그대로
 * 넣었고 DB CHECK(숫자 9~11자리)가 이를 500으로 거부했다. 빈 값을 여기서
 * undefined로 걷어내 그 경로를 없앤다.
 */
const optionalPhone = z.preprocess(
  (v) => (v === '' || v == null ? undefined : v),
  z
    .string()
    .transform((v) => v.replace(/[^0-9]/g, ''))
    .refine((v) => /^0\d{8,10}$/.test(v), '전화번호 형식이 올바르지 않습니다')
    .optional(),
)

/** 알림 수신 번호. 휴대폰만 가능(DB CHECK와 동일 규칙). */
const mobilePhone = z
  .string()
  .transform((v) => v.replace(/[^0-9]/g, ''))
  .refine((v) => /^01[016789]\d{7,8}$/.test(v), '휴대폰 번호를 입력하세요')

// ── 기업 ────────────────────────────────────────────────────

export const createCompanyRequest = z
  .object({
    name: z.string().min(1).max(100),
    bizRegNo,
    ceoName: z.string().max(50).optional(),
    address: z.string().max(200).optional(),
    contractStartAt: z.string().date(), // 'YYYY-MM-DD'
    contractEndAt: z.string().date().optional(),
    paymentDueDays: z.number().int().positive(),
    notifyPhone: mobilePhone.optional(),
    note: z.string().max(1000).optional(),
    billingContact: z
      .object({
        name: z.string().min(1).max(50),
        email,
        phone: optionalPhone,
      })
      .optional(),
    // 지인영업 수동 가입처리(BIZ-85): 관리자 계정을 함께 만들어 초대 링크를 발급한다.
    admin: z
      .object({
        name: z.string().min(1).max(50),
        email,
        phone: optionalPhone,
      })
      .optional(),
    // 초대한 사람(추천인) 코드 — 있으면 새 관리자를 그 추천인에 귀속한다(BIZ-132 연계).
    referralCode: z.string().max(32).optional(),
  })
  // 계약 종료일이 시작일보다 앞설 수 없다. 계약에서 걸러 DB CHECK(23514)가
  // 500으로 새는 것을 막는다.
  .refine((d) => !d.contractEndAt || d.contractEndAt >= d.contractStartAt, {
    message: '계약 종료일은 시작일 이후여야 합니다',
    path: ['contractEndAt'],
  })
export type CreateCompanyRequest = z.infer<typeof createCompanyRequest>

export const bizVerifyStatus = z.enum(['UNVERIFIED', 'VERIFIED', 'FAILED', 'EXEMPT'])
export type BizVerifyStatus = z.infer<typeof bizVerifyStatus>

/** 운영자 수동 검증 처리(BIZ-85). 확인됨/실패/미확인 되돌리기 + 메모. */
export const setCompanyVerifyRequest = z.object({
  status: z.enum(['VERIFIED', 'FAILED', 'UNVERIFIED']),
  note: z.string().max(300).nullable().optional(),
})
export type SetCompanyVerifyRequest = z.infer<typeof setCompanyVerifyRequest>

export const companyResponse = z.object({
  id: z.number().int(),
  name: z.string(),
  subjectType,
  // 셀프가입(BIZ-67) 개인·단체는 사업자번호·계약이 없어 nullable이다.
  bizRegNo: z.string().nullable(),
  approvalStatus,
  postpaidStatus,
  ceoName: z.string().nullable(),
  address: z.string().nullable(),
  contractStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']),
  contractStartAt: z.string().nullable(),
  contractEndAt: z.string().nullable(),
  paymentDueDays: z.number().int(),
  notifyPhone: z.string().nullable(),
  note: z.string().nullable(),
  // 주문 할인율·적립율 회사 오버라이드(BIZ-74). 퍼센트(0~100). null이면 전역 기본값 사용.
  pointDiscountRatePercent: z.number().nullable(),
  pointEarnRatePercent: z.number().nullable(),
  // 후불 신청·자동정지 시각(BIZ-116). 상태는 postpaidStatus가 정본이고 이건 기록용.
  postpaidRequestedAt: z.string().nullable(),
  postpaidSuspendedAt: z.string().nullable(),
  postpaidDecidedAt: z.string().nullable(),
  // 사업자 검증 축(BIZ-85). 후불 자격과 독립.
  bizVerifyStatus: bizVerifyStatus,
  bizVerifiedAt: z.string().nullable(),
  /** 후불 신청 사업자등록증 사본 보유 여부용 키 유무(BIZ-85). 값은 admin만, 열람은 서명 URL. */
  postpaidDocKey: z.string().nullable(),
})
export type CompanyResponse = z.infer<typeof companyResponse>

/**
 * 기업 정보 수정(운영자). 사업자등록번호는 조직 식별자라 변경하지 않는다.
 * 계약 상태·기간·청구 조건·알림 번호를 여기서 고친다(BIZ-57).
 * 전체 치환(PUT)이라 화면은 현재 값을 채워 넣고 통째로 보낸다.
 */
export const updateCompanyRequest = z
  .object({
    name: z.string().min(1).max(100),
    ceoName: z.string().max(50).optional(),
    address: z.string().max(200).optional(),
    contractStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']),
    contractStartAt: z.string().date(),
    contractEndAt: z.string().date().optional(),
    paymentDueDays: z.number().int().positive(),
    notifyPhone: mobilePhone.optional(),
    note: z.string().max(1000).optional(),
    // 주문 할인율·적립율 회사 오버라이드(BIZ-74) — 퍼센트(0~100). null/빈값이면 전역 기본값 사용.
    pointDiscountRatePercent: z.number().min(0).max(100).nullable().optional(),
    pointEarnRatePercent: z.number().min(0).max(100).nullable().optional(),
  })
  .refine((d) => !d.contractEndAt || d.contractEndAt >= d.contractStartAt, {
    message: '계약 종료일은 시작일 이후여야 합니다',
    path: ['contractEndAt'],
  })
export type UpdateCompanyRequest = z.infer<typeof updateCompanyRequest>

/**
 * 기업 관리자가 '내 회사'에서 고칠 수 있는 연락 정보(BIZ-64).
 * 계약 상태·기간·청구 마감/지급 기한·사업자번호는 운영자 계약 소관이라 여기서 못 바꾼다.
 * billingContact: 값이면 upsert, null이면 삭제, 생략이면 유지.
 */
export const updateMyCompanyRequest = z.object({
  ceoName: z.string().max(50).optional(),
  address: z.string().max(200).optional(),
  notifyPhone: mobilePhone.optional(),
  billingContact: z
    .object({ name: z.string().min(1).max(50), email, phone: optionalPhone })
    .nullable()
    .optional(),
})
export type UpdateMyCompanyRequest = z.infer<typeof updateMyCompanyRequest>

/** 기업 관리자가 자기 회사 구성원을 초대(BIZ-64). companyId는 서버가 세션에서 강제한다. */
export const inviteCompanyMemberRequest = z.object({
  email,
  name: z.string().min(1).max(50),
  role: z.enum(['COMPANY_ADMIN', 'COMPANY_MEMBER']),
  // 있으면 초대 링크를 알림톡으로 자동 발송한다(BIZ-56). 없으면 링크 수동 전달.
  phone: mobilePhone.optional(),
})
export type InviteCompanyMemberRequest = z.infer<typeof inviteCompanyMemberRequest>

/** 구성원 활성/비활성 토글(BIZ-64). */
export const updateMemberStatusRequest = z.object({
  status: z.enum(['ACTIVE', 'DEACTIVATED']),
})
export type UpdateMemberStatusRequest = z.infer<typeof updateMemberStatusRequest>

/** 구성원 역할 변경(BIZ-72). 기업 계정 안에서만 관리자↔담당자. */
export const updateMemberRoleRequest = z.object({
  role: z.enum(['COMPANY_ADMIN', 'COMPANY_MEMBER']),
})
export type UpdateMemberRoleRequest = z.infer<typeof updateMemberRoleRequest>

// ── 화환 업체 ───────────────────────────────────────────────

export const createVendorRequest = z.object({
  name: z.string().min(1).max(100),
  bizRegNo,
  ceoName: z.string().max(50).optional(),
  address: z.string().max(200).optional(),
  contractStartAt: z.string().date(),
  bankName: z.string().max(50).optional(),
  bankAccount: z.string().max(50).optional(),
  accountHolder: z.string().max(50).optional(),
  notifyPhone: mobilePhone.optional(),
  note: z.string().max(1000).optional(),
})
export type CreateVendorRequest = z.infer<typeof createVendorRequest>

export const vendorResponse = z.object({
  id: z.number().int(),
  name: z.string(),
  bizRegNo: z.string(),
  ceoName: z.string().nullable(),
  address: z.string().nullable(),
  contractStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']),
  contractStartAt: z.string(),
  bankName: z.string().nullable(),
  bankAccount: z.string().nullable(),
  accountHolder: z.string().nullable(),
  notifyPhone: z.string().nullable(),
  note: z.string().nullable(),
})
export type VendorResponse = z.infer<typeof vendorResponse>

/**
 * 화환 업체 정보 수정(운영자). 사업자등록번호는 변경하지 않는다.
 * 계약 상태·정산 계좌·알림 번호를 여기서 고친다(BIZ-57). 전체 치환(PUT).
 */
export const updateVendorRequest = z.object({
  name: z.string().min(1).max(100),
  ceoName: z.string().max(50).optional(),
  address: z.string().max(200).optional(),
  contractStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']),
  contractStartAt: z.string().date(),
  bankName: z.string().max(50).optional(),
  bankAccount: z.string().max(50).optional(),
  accountHolder: z.string().max(50).optional(),
  notifyPhone: mobilePhone.optional(),
  note: z.string().max(1000).optional(),
})
export type UpdateVendorRequest = z.infer<typeof updateVendorRequest>

// ── 목록·상세 (운영자 관리 화면) ─────────────────────────────

/** 목록 행. 상세보다 가벼운 필드만. */
export const companyListItem = z.object({
  id: z.number().int(),
  name: z.string(),
  subjectType,
  bizRegNo: z.string().nullable(),
  approvalStatus,
  postpaidStatus,
  bizVerifyStatus,
  contractStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']),
  memberCount: z.number().int(),
  createdAt: z.string(), // 가입일(ISO). 목록 기본 정렬 기준 — 가입일 내림차순.
  /** 후불 전환 신청 시각(BIZ-116). 있으면 '후불 신청 대기'. */
  postpaidRequestedAt: z.string().nullable(),
})
export type CompanyListItem = z.infer<typeof companyListItem>

/** 운영자가 가승인(PENDING) 기업을 승인/반려한다(BIZ-68). */
export const companyApprovalRequest = z.object({
  decision: z.enum(['APPROVE', 'REJECT']),
})
export type CompanyApprovalRequest = z.infer<typeof companyApprovalRequest>

/**
 * 운영자가 후불 전환 신청을 승인/반려한다(BIZ-116 → BIZ-149). 승인=REQUESTED→ACTIVE(후불 개방),
 * 반려=REQUESTED→REJECTED(선불 유지, 다시 신청 가능). 가입 승인(approvalStatus)은 건드리지 않는다.
 */
export const postpaidDecisionRequest = z.object({
  decision: z.enum(['APPROVE', 'REJECT']),
  reason: z.string().max(500).optional(),
})
export type PostpaidDecisionRequest = z.infer<typeof postpaidDecisionRequest>

/** 후불 모니터링 한 행(BIZ-116): 신청 대기 / 후불 정지 / 미납 있는 후불 회사. */
export const postpaidMonitorItem = z.object({
  companyId: z.number().int(),
  name: z.string(),
  bizRegNo: z.string().nullable(),
  approvalStatus,
  postpaidStatus,
  /** 국세청 사업자 검증 상태(BIZ-85). 후불 승인 판단 보조. */
  bizVerifyStatus,
  postpaidRequestedAt: z.string().nullable(),
  postpaidSuspendedAt: z.string().nullable(),
  /** 유예(결제기한+7일) 지난 미납 청구 건수·합계. */
  overdueCount: z.number().int(),
  overdueAmount: z.number().int(),
})
export type PostpaidMonitorItem = z.infer<typeof postpaidMonitorItem>

export const postpaidMonitorResponse = z.object({ items: z.array(postpaidMonitorItem) })
export type PostpaidMonitorResponse = z.infer<typeof postpaidMonitorResponse>

export const companyListResponse = z.object({ companies: z.array(companyListItem) })
export type CompanyListResponse = z.infer<typeof companyListResponse>

/** 조직 구성원. 초대 중(INVITED)과 활성(ACTIVE)을 함께 보여준다. */
export const orgMember = z.object({
  id: z.number().int(),
  email: z.string(),
  name: z.string(),
  role,
  status: z.enum(['INVITED', 'ACTIVE', 'SUSPENDED', 'DEACTIVATED']),
})
export type OrgMember = z.infer<typeof orgMember>

export const companyBillingContact = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
})
export type CompanyBillingContact = z.infer<typeof companyBillingContact>

export const companyDetailResponse = z.object({
  company: companyResponse,
  billingContact: companyBillingContact.nullable(),
  members: z.array(orgMember),
  /** 추천 효력 수동 만료 스위치(BIZ-132). true=만료(신규 귀속·판매 적립 중단). */
  referralDisabled: z.boolean(),
})
export type CompanyDetailResponse = z.infer<typeof companyDetailResponse>

/** 추천 효력 스위치 변경 (BIZ-132, 운영자). disabled=true면 만료. */
export const setCompanyReferralRequest = z.object({ disabled: z.boolean() })
export type SetCompanyReferralRequest = z.infer<typeof setCompanyReferralRequest>

export const vendorListItem = z.object({
  id: z.number().int(),
  name: z.string(),
  bizRegNo: z.string(),
  contractStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']),
  memberCount: z.number().int(),
})
export type VendorListItem = z.infer<typeof vendorListItem>

export const vendorListResponse = z.object({ vendors: z.array(vendorListItem) })
export type VendorListResponse = z.infer<typeof vendorListResponse>

export const vendorDetailResponse = z.object({
  vendor: vendorResponse,
  members: z.array(orgMember),
})
export type VendorDetailResponse = z.infer<typeof vendorDetailResponse>

// ── 초대 ────────────────────────────────────────────────────

/**
 * 사용자 초대. app_user를 INVITED로 만들고 invitation 토큰을 발급한다.
 *
 * 역할에 맞는 소속을 함께 지정해야 한다.
 *  - COMPANY_ADMIN / COMPANY_MEMBER → companyId 필수
 *  - VENDOR_MEMBER → vendorId 필수
 *  - OPERATOR → 소속 없음
 * 소속 검증은 서버가 role과 함께 확인한다(잘못된 조합은 400).
 */
export const inviteUserRequest = z.object({
  email,
  name: z.string().min(1).max(50),
  role,
  companyId: z.number().int().optional(),
  vendorId: z.number().int().optional(),
  // 있으면 초대 링크를 알림톡으로 자동 발송한다(BIZ-56). 없으면 링크 수동 전달.
  phone: mobilePhone.optional(),
})
export type InviteUserRequest = z.infer<typeof inviteUserRequest>

/**
 * 초대 생성 응답.
 *
 * MVP에서는 이메일·알림톡 발송이 아직 없으므로 초대 링크를 응답으로 돌려줘
 * 운영자가 수동으로 전달하게 한다. M4 알림 트랙과 합류하면 자동 발송으로 바뀐다.
 * 링크에는 원문 토큰이 들어 있다. DB에는 해시만 저장된다.
 */
export const inviteUserResponse = z.object({
  userId: z.number().int(),
  email: z.string(),
  role,
  inviteUrl: z.string(),
  expiresAt: z.string(),
})
export type InviteUserResponse = z.infer<typeof inviteUserResponse>

export const OrgErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  DUPLICATE_BIZ_REG_NO: 'DUPLICATE_BIZ_REG_NO',
  DUPLICATE_EMAIL: 'DUPLICATE_EMAIL',
  ROLE_SCOPE_MISMATCH: 'ROLE_SCOPE_MISMATCH', // role과 companyId/vendorId 조합이 틀림
  ORG_NOT_FOUND: 'ORG_NOT_FOUND',
  NOT_PENDING: 'NOT_PENDING', // 가승인(PENDING) 상태가 아닌 회사를 승인/반려 시도 (BIZ-68)
  FORBIDDEN: 'FORBIDDEN',
  // 후불 전환 신청/처리 (BIZ-116)
  POSTPAID_NOT_ELIGIBLE: 'POSTPAID_NOT_ELIGIBLE', // 법인 사업자가 아니라 신청 자격 없음
  POSTPAID_SUSPENDED: 'POSTPAID_SUSPENDED', // 미납 정지 중 — 완납하면 자동 재개되니 신청할 게 없다
  POSTPAID_ALREADY_REQUESTED: 'POSTPAID_ALREADY_REQUESTED', // 이미 후불 신청함(검토 대기)
  ALREADY_POSTPAID: 'ALREADY_POSTPAID', // 이미 후불(APPROVED)
  NO_POSTPAID_REQUEST: 'NO_POSTPAID_REQUEST', // 신청 이력 없는 회사를 승인/반려 시도
} as const
export type OrgErrorCode = (typeof OrgErrorCode)[keyof typeof OrgErrorCode]
